import { API_ROUTES } from '@/constants/apiRoutes';
import { IdempotentRequest } from '@/types/common';
import {
  CompanyDetails,
  CompanyForSelectItem,
  CompanyListResponse,
  CompanyFormFields,
} from '@/types/companies';
import { api, formatCurrencyForPost } from '@/utils/api';

export const getCompaniesListService = async (queryParams: string) => {
  const { data } = await api<CompanyListResponse>({
    url: `${API_ROUTES.COMPANIES}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export async function getCompanyDetails(uuid: string) {
  const { data } = await api<CompanyDetails>({
    url: `${API_ROUTES.COMPANIES}${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export async function getCompanyListForSelectionService(queryParams = '') {
  const { data } = await api<CompanyForSelectItem[]>({
    url: `${API_ROUTES.COMPANIES}for-selection?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export const deactivateCompanyService = async (
  uuid: string,
  preflight: boolean,
  teammateId?: string,
) => {
  const payload = teammateId
    ? {
        preflight,
        new_teammate_id: teammateId,
      }
    : { preflight };

  const { data } = await api<{
    mustReplaceTeammate: boolean;
  }>({
    url: `${API_ROUTES.COMPANIES}${uuid}/deactivate`,
    method: 'post',
    version: 2,
    data: payload,
  });

  return data;
};

export async function reactivateCompanyService(uuid: string) {
  const { data } = await api({
    url: `${API_ROUTES.COMPANIES}${uuid}/reactivate`,
    method: 'post',
    version: 2,
  });

  return data;
}

export async function updateCompanySavingsInterestRate(
  uuid: string,
  rateSavingInterest: number,
) {
  const { data } = await api({
    url: `${API_ROUTES.COMPANIES}${uuid}/rate-saving-interest/`,
    method: 'post',
    version: 1,
    data: {
      rate_saving_interest: rateSavingInterest,
    },
  });

  return data;
}

function formatCompanyPayload(companyData: CompanyFormFields) {
  return {
    uuid: companyData.uuid,
    name: companyData.name,
    branding_theme_id: companyData.brandingThemeId,
    advisor_office_type: companyData.advisorOfficeType,
    relationship_manager_id: companyData.relationshipManagerId,
    lending_enabled: companyData.lendingEnabled,
    teammate_perms: [
      ...(companyData.teammateCreatePerm ? [0] : []),
      ...(companyData.teammateUpdatePerm ? [1] : []),
      ...(companyData.teammateDeactivateReactivatePerm ? [2] : []),
      ...(companyData.divisionCreatePerm ? [3] : []),
      ...(companyData.divisionUpdatePerm ? [4] : []),
      ...(companyData.divisionDeletePerm ? [5] : []),
    ],
    ...(companyData.rateSavingInterest && {
      rate_saving_interest: parseFloat(companyData.rateSavingInterest),
    }),
    ...(companyData.minimumInitDeposit && {
      minimum_init_deposit: formatCurrencyForPost(
        companyData.minimumInitDeposit,
      ),
    }),
    alias: companyData.alias,
    // Fee fields: null = use program setting, value = override
    fee_incoming_domestic: companyData.feeIncomingDomestic
      ? formatCurrencyForPost(companyData.feeIncomingDomestic)
      : null,
    fee_outcome_domestic: companyData.feeOutcomeDomestic
      ? formatCurrencyForPost(companyData.feeOutcomeDomestic)
      : null,
    fee_monthly_saving_account: companyData.feeMonthlySavingAccount
      ? formatCurrencyForPost(companyData.feeMonthlySavingAccount)
      : null,
    ...(companyData.debitCardProgramId && {
      debit_card_program_id: companyData.debitCardProgramId,
    }),
  };
}

export async function createCompanyService(
  companyData: IdempotentRequest<CompanyFormFields>,
) {
  const apiData = formatCompanyPayload(companyData);

  const { data } = await api<string>({
    url: `${API_ROUTES.COMPANIES}`,
    method: 'post',
    version: 2,
    data: apiData,
    idempotencyKey: companyData.idempotencyKey,
  });

  return data;
}

export async function editCompanyService(
  companyData: IdempotentRequest<CompanyFormFields>,
) {
  const apiData = formatCompanyPayload(companyData);

  const { data } = await api<CompanyDetails>({
    url: `${API_ROUTES.COMPANIES}${companyData.uuid}`,
    method: 'put',
    version: 2,
    data: apiData,
    idempotencyKey: companyData.idempotencyKey,
  });

  return data.uuid;
}
